import { PrayerTime } from "@/types/page";

// Ensure time is in valid time format
export const isValidTime = (time: string | any): boolean => {
  if (typeof time !== 'string') {
    return false;
  }
  return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
};

// Initialize prayer times from content
export const initializePrayerTimes = (content: any): {
  prayerTimes: PrayerTime[],
  blockTitle: string,
  animationSpeed: number,
  isLegacyFormat: boolean,
  originalContentFormat: any
} => {
  // Initial values
  let prayerTimes: PrayerTime[] = [];
  let blockTitle = "זמני תפילה";
  let animationSpeed = 20;
  let isLegacyFormat = false;
  let originalContentFormat = null;
  
  console.log("Initializing prayer times with content:", content);
  
  // Detect data format type and keep a record of the original format
  let detectedFormat = "unknown";
  
  // First try the new format with sections
  if (content?.sections?.[0]?.prayerTimes?.length > 0) {
    detectedFormat = "sections";
    originalContentFormat = {
      type: "sections",
      data: {
        sections: content.sections,
        blockTitle: content.blockTitle,
        animationSpeed: content.animationSpeed
      }
    };
    
    prayerTimes = content.sections[0].prayerTimes.map((pt: any) => ({
      id: pt.id || `pt-${Date.now()}-${Math.random().toString(36).substring(2)}`,
      title: pt.title || '',
      time: typeof pt.time === 'string' ? pt.time : String(pt.time || "08:00"),
      customTitle: pt.customTitle,
      isShabbat: pt.isShabbat
    }));
    
    blockTitle = content.blockTitle || content.sections[0].title || "זמני תפילה";
    animationSpeed = content.animationSpeed || 20;
    
    return { prayerTimes, blockTitle, animationSpeed, isLegacyFormat, originalContentFormat };
  }
  
  // Then try the array format
  if (content?.prayerTimes?.length > 0) {
    detectedFormat = "array";
    originalContentFormat = {
      type: "array",
      data: {
        prayerTimes: content.prayerTimes,
        blockTitle: content.blockTitle,
        animationSpeed: content.animationSpeed
      }
    };
    
    prayerTimes = content.prayerTimes.map((pt: any) => ({
      id: pt.id || `pt-${Date.now()}-${Math.random().toString(36).substring(2)}`,
      title: pt.title || '',
      time: typeof pt.time === 'string' ? pt.time : String(pt.time || "08:00"),
      customTitle: pt.customTitle,
      isShabbat: pt.isShabbat
    }));
    
    blockTitle = content.blockTitle || "זמני תפילה";
    animationSpeed = content.animationSpeed || 20;
    
    return { prayerTimes, blockTitle, animationSpeed, isLegacyFormat, originalContentFormat };
  }
  
  // Finally, try the legacy format with individual fields
  const legacyPrayerTimes: PrayerTime[] = [];
  let hasLegacyFields = false;
  
  // Map of legacy field names to titles
  const legacyFields = {
    shacharit: "שחרית",
    mincha: "מנחה",
    maariv: "ערבית",
    vatikin: "ותיקין",
    sunrise: "זריחה",
    sunset: "שקיעה",
    midday: "חצות היום",
    midnight: "חצות הלילה",
    shabbatIn: "כניסת שבת",
    shabbatOut: "יציאת שבת"
  };
  
  // Create a copy of the original legacy content
  const legacyContent = {...content};
  
  // Check each legacy field
  Object.entries(legacyFields).forEach(([field, title]) => {
    if (content?.[field] && content[field].toString().trim() !== '') {
      hasLegacyFields = true;
      // Store the legacy field info in a temporary variable
      const legacyField = field;
      legacyPrayerTimes.push({
        id: `pt-${field}-${Date.now()}`,
        title: title,
        time: typeof content[field] === 'string' ? content[field] : String(content[field]),
        isShabbat: field.startsWith('shabbat')
        // Removed legacyFieldName property as it's not in the PrayerTime interface
      });
    }
  });
  
  if (hasLegacyFields) {
    detectedFormat = "legacy";
    originalContentFormat = {
      type: "legacy",
      data: legacyContent
    };
    isLegacyFormat = true;
    
    prayerTimes = legacyPrayerTimes;
    blockTitle = content.blockTitle || "זמני תפילה";
    animationSpeed = content.animationSpeed || 20;
    
    return { prayerTimes, blockTitle, animationSpeed, isLegacyFormat, originalContentFormat };
  }
  
  // If no data found, set format to new
  detectedFormat = "new";
  originalContentFormat = {
    type: "sections",
    data: {
      sections: [{
        id: "prayer-times",
        title: "זמני תפילה",
        prayerTimes: []
      }],
      blockTitle: "זמני תפילה",
      animationSpeed: 20
    }
  };
  
  blockTitle = "זמני תפילה";
  
  console.log("Initialized with format:", detectedFormat, "Prayer times:", prayerTimes);
  
  return { prayerTimes, blockTitle, animationSpeed, isLegacyFormat, originalContentFormat };
};
